/**
 * useReadinessDeepLink — shared deep-link router for ReadinessFinding
 * primary actions.
 *
 * Plan §"One button per issue (no navigation maze)" promises that every
 * finding card's primary action navigates the user *to where the edit
 * happens.* This hook is the one place that wiring lives, so every host
 * (AssemblyInspectorPanel, DestinationNode inspector,
 * PipelineOutputsDrawer, etc.) routes through identical logic — no
 * inconsistent deep-link behavior across surfaces.
 *
 * Action routing:
 *
 *   - fix_in_mapping  → navigate to /batches/:batchId/mapping with the
 *                       target field encoded in the query string. The
 *                       mapping canvas page reads ?focus=table.field
 *                       and scrolls/highlights the matching row.
 *   - review_data     → navigate to /batches/:batchId/mapping with a
 *                       data-explorer focus param so the user lands on
 *                       the unmatched-rows panel for the offending
 *                       source table. (Real "data explorer" panel
 *                       integration is a follow-up; current behavior is
 *                       to land on the mapping canvas with a hint param
 *                       the canvas can opt-in to honoring.)
 *   - adjust_schema   → no in-product target schema editor today; opens
 *                       the same mapping route with a hint flag so the
 *                       user lands somewhere useful instead of a dead
 *                       end. Tooltipped accordingly.
 *   - none            → no-op (info-only findings have no action).
 *
 * The returned ``handlePrimaryAction`` is a stable callback safe to pass
 * directly to ``<ReadinessPanel onPrimaryAction>``.
 */
import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import type { ReadinessFinding } from '../../../types/readiness';

export interface UseReadinessDeepLinkResult {
  handlePrimaryAction: (finding: ReadinessFinding) => void;
}

export function useReadinessDeepLink(): UseReadinessDeepLinkResult {
  const navigate = useNavigate();
  const { batchId } = useParams<{ batchId: string }>();

  const handlePrimaryAction = useCallback(
    (finding: ReadinessFinding) => {
      if (!batchId) return;

      // Read deep-link payload — both Python and TS sides agree that
      // ``primary_action_target`` carries the user-domain coordinates
      // (target_table + target_field, or table + unmatched_only).
      const target = finding.primary_action_target ?? {};
      const params = new URLSearchParams();

      if (typeof target.target_table === 'string' && target.target_table) {
        params.set('focus_table', target.target_table);
      }
      if (typeof target.target_field === 'string' && target.target_field) {
        params.set('focus_field', target.target_field);
      }
      if (typeof target.level_path === 'string' && target.level_path) {
        params.set('focus_level', target.level_path);
      }
      // ``finding_code`` lets the receiving page open the right
      // context (e.g. value-domain editor for a codelist finding).
      params.set('finding', finding.code);
      params.set('action', finding.primary_action);

      // Plan §"One button per issue" — three distinct landing pages:
      //
      //   fix_in_mapping  → /mapping. The mapping canvas focuses the
      //                     offending target field; receiver lives in
      //                     CanvasMappingEditor + ReviewPane.
      //   review_data     → /workspace. The workspace canvas opens
      //                     the relevant source table so the user can
      //                     inspect orphan rows / sample data /
      //                     decide whether to fix at source.
      //   adjust_schema   → /mapping with an ``edit_schema=true`` hint
      //                     so the mapping canvas opens the target
      //                     schema editor for the field. (No
      //                     standalone schema-edit route exists today.)
      switch (finding.primary_action) {
        case 'fix_in_mapping':
          navigate(`/batches/${batchId}/mapping?${params.toString()}`);
          return;
        case 'review_data':
          // Workspace canvas can render a data-preview drawer for the
          // referenced source table. The receiver there opens the
          // relevant row when ``focus_table`` is present.
          navigate(`/batches/${batchId}/workspace?${params.toString()}`);
          return;
        case 'adjust_schema':
          params.set('edit_schema', 'true');
          navigate(`/batches/${batchId}/mapping?${params.toString()}`);
          return;
        case 'none':
        default:
          return;
      }
    },
    [navigate, batchId],
  );

  return { handlePrimaryAction };
}

export default useReadinessDeepLink;
